import {
  VARIANT_IMPORT_MODE_NONE,
  ROW_TYPE_PARENT,
  ROW_TYPE_VARIANT,
} from './variantImportConstants';

export const PRODUCT_ONLY_FIELDS = [
  'descriptionHtml',
  'vendor',
  'productType',
  'keywords',
  'handle',
  'seoTitle',
  'seoDescription',
  'category',
  'status',
];

export const VARIANT_ONLY_FIELDS = [
  'compareAtPrice',
  'unitCost',
  'barcode',
  'inventoryPolicy',
  'weight',
  'taxCode',
  'availableForSale',
  'description',
];

export const SHARED_FIELDS = [
  'sku',
  'title',
  'price',
  'taxable',
  'tracked',
  'requiresShipping',
  'countryCodeOfOrigin',
  'harmonizedSystemCode',
];

// PCS-11321 system CSV columns — not in the mapping dropdown; auto-mapped by exact header.
export const SYSTEM_CSV_FIELDS = {
  recordId: 'Toriut Record ID',
  mainImage: 'Toriut Main Image',
  media: 'Toriut Media',
};

export const SYSTEM_CSV_FIELD_KEYS = Object.keys(SYSTEM_CSV_FIELDS);

const SYSTEM_CSV_HEADER_SET = new Set(
  Object.values(SYSTEM_CSV_FIELDS).map((label) => String(label).trim().toLowerCase()),
);

export function isSystemCsvField(fieldKey) {
  return SYSTEM_CSV_FIELD_KEYS.includes(fieldKey);
}

/** True when a CSV header is a reserved Toriut system column (exact name, case-insensitive). */
export function isSystemCsvHeader(columnName) {
  return SYSTEM_CSV_HEADER_SET.has(String(columnName ?? '').trim().toLowerCase());
}

export function getAvailableOptionColumns(fileColumns = [], parentSkuColumn) {
  return fileColumns.filter(
    (columnName) => columnName
      && columnName !== parentSkuColumn
      && !isSystemCsvHeader(columnName),
  );
}

const BUILTIN_FIELD_SCOPES = new Map([
  ...PRODUCT_ONLY_FIELDS.map((field) => [field, 'product']),
  ...VARIANT_ONLY_FIELDS.map((field) => [field, 'variant']),
  ...SHARED_FIELDS.map((field) => [field, 'shared']),
]);

export function getBuiltinFieldScope(fieldKey) {
  return BUILTIN_FIELD_SCOPES.get(fieldKey) || null;
}

export function isBuiltinFieldKey(fieldKey) {
  return BUILTIN_FIELD_SCOPES.has(fieldKey) || isSystemCsvField(fieldKey);
}

export function formatScopedCustomMappingValue(title, scope) {
  return `meta.${scope}.${title}`;
}

export function parseScopedCustomMapping(value) {
  if (typeof value !== 'string' || !value.startsWith('meta.')) {
    return null;
  }

  const rest = value.slice(5);
  const dotIdx = rest.indexOf('.');

  if (dotIdx <= 0) {
    return null;
  }

  const scope = rest.slice(0, dotIdx);
  const title = rest.slice(dotIdx + 1);

  if ((scope !== 'product' && scope !== 'variant') || !title || title.includes('#')) {
    return null;
  }

  return { scope, title };
}

export function isCustomFieldMapping(value) {
  return parseScopedCustomMapping(value) !== null;
}

export function getFieldScopeTitleKey(scope, title) {
  return `${scope}:${title}`;
}

export function isFieldApplicable(fieldKey, {
  rowType,
  variantImportMode = VARIANT_IMPORT_MODE_NONE,
  isCustomField = false,
  customScope,
} = {}) {
  if (isSystemCsvField(fieldKey)) {
    return true;
  }

  if (isCustomField) {
    if (variantImportMode === VARIANT_IMPORT_MODE_NONE) {
      return true;
    }
    if (rowType === ROW_TYPE_PARENT) {
      return customScope === 'product';
    }
    if (rowType === ROW_TYPE_VARIANT) {
      return customScope === 'variant';
    }
    return customScope === 'product';
  }

  const scope = getBuiltinFieldScope(fieldKey);
  if (!scope) {
    return false;
  }

  if (variantImportMode === VARIANT_IMPORT_MODE_NONE) {
    return true;
  }

  if (scope === 'shared') {
    return true;
  }

  if (rowType === ROW_TYPE_PARENT) {
    return scope === 'product' || scope === 'variant' || scope === 'shared';
  }

  if (rowType === ROW_TYPE_VARIANT) {
    return scope === 'variant';
  }

  return scope === 'product' || scope === 'variant';
}

export function buildFieldByScopeTitle(items = []) {
  return items.reduce((acc, item) => {
    if (item.type === 'separator') {
      return acc;
    }
    acc[getFieldScopeTitleKey(item.scope, item.title)] = item;
    return acc;
  }, {});
}

export function getDuplicateAttributeTitles(items = []) {
  const titlesByScope = {
    product: new Set(),
    variant: new Set(),
  };

  items.forEach((item) => {
    if (item.type === 'separator') {
      return;
    }
    titlesByScope[item.scope]?.add(item.title);
  });

  return new Set(
    [...titlesByScope.product].filter((title) => titlesByScope.variant.has(title)),
  );
}
